import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

const StyledVideoWrapper = styled.div`
  position: relative;
  display: inline-flex;
  max-width: 100%;
  max-height: inherit;

  video {
    display: block;
    max-width: 100%;
    max-height: inherit;
  }
`;

const StyledPlayPauseButton = styled.button<{ $isPlaying: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  padding: 0;
  border: 4px solid var(--gray-medium-dark);
  background: var(--gray-deep);
  color: var(--white-main);
  cursor: pointer;
  transition: opacity 0.2s ease;

  && {
    border-radius: 50%;
  }

  i {
    font-size: var(--medium-font-size);
  }

  ${({ $isPlaying }) =>
    $isPlaying &&
    css`
      opacity: 0;

      ${StyledVideoWrapper}:hover &,
      &:focus-visible {
        opacity: 1;
      }
    `}
`;

type ContentVideoProps = {
  src: string;
  withControls?: boolean;
};

const muteVideo = (video: HTMLVideoElement | null) => {
  if (!video) return;
  video.defaultMuted = true;
  video.muted = true;
};

const LoopingVideo: React.FC<{ src: string }> = ({ src }) => (
  <StyledVideoWrapper>
    <video
      ref={muteVideo}
      src={src}
      autoPlay
      muted
      loop
      playsInline
      disablePictureInPicture
    />
  </StyledVideoWrapper>
);

const ControlledVideo: React.FC<{ src: string }> = ({ src }) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlayback = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play()?.catch(() => setIsPlaying(false));
    }
  }, [isPlaying]);

  return (
    <StyledVideoWrapper>
      <video
        ref={videoRef}
        src={src}
        autoPlay
        playsInline
        disablePictureInPicture
        onClick={togglePlayback}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
      />
      <StyledPlayPauseButton
        type="button"
        $isPlaying={isPlaying}
        aria-label={
          isPlaying ? t('content.video.pause') : t('content.video.play')
        }
        onClick={togglePlayback}
      >
        <i className={isPlaying ? 'pi pi-pause' : 'pi pi-play'} />
      </StyledPlayPauseButton>
    </StyledVideoWrapper>
  );
};

export const ContentVideo: React.FC<ContentVideoProps> = ({
  src,
  withControls = false,
}) =>
  withControls ? (
    <ControlledVideo key={src} src={src} />
  ) : (
    <LoopingVideo key={src} src={src} />
  );
